'use client';

import { ErrorCode } from '@ahm/api-wrappers-core';
import { Button, Modal, Stack, Text, rem } from '@mantine/core';
import dayjs, { extend } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useEffect, useState, useMemo, useCallback } from 'react';
import type { Locales } from '../locales';
import { actionsLocales } from '../locales/actions';
import { authLocales } from '../locales/auth';
import createTranslations from '../locales/createTranslations';

extend(duration);

const AHAMOVE_HOTLINE = '1900545411';

const formatTime = (seconds: number) =>
  dayjs.duration(seconds, 'seconds').format('mm:ss');
const OTP_TIME_REGEX = /\d{1,2}:\d{2}s? nữa/;
const MOBILE_REGEX =
  /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

export interface BannedModalProps {
  opened: boolean;
  onClose: () => void;
  title?: string;
  content?: string;
  countdownInSecond?: number;
  onConfirm?: () => void;
  lang?: keyof Locales;
  errorCode?: string;
}

export function BannedModal({
  opened,
  onClose,
  content,
  countdownInSecond,
  onConfirm,
  title,
  lang = 'vi',
  errorCode,
}: BannedModalProps) {
  const tActions = useMemo(
    () => createTranslations({ locales: actionsLocales, lang }),
    [lang]
  );
  const tAuth = useMemo(
    () => createTranslations({ locales: authLocales, lang }),
    [lang]
  );
  const [secondsLeft, setSecondsLeft] = useState(countdownInSecond ?? 0);

  const confirmLabel =
    errorCode === 'USER_DEACTIVATED'
      ? tAuth('contactWithCustomerSupport')
      : errorCode === ErrorCode.DeactivatedUserWrongPasscode.toString()
        ? tAuth('getPasswordResetLink')
        : tActions('got_it');

  const needCancelButton = confirmLabel === tActions('got_it');

  useEffect(() => {
    if (!countdownInSecond) return;
    setSecondsLeft(countdownInSecond);
    const timerId = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerId);
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerId);
  }, [countdownInSecond, onClose]);

  const detailContent = useMemo(() => {
    if (countdownInSecond) {
      if (typeof content === 'string' && OTP_TIME_REGEX.test(content)) {
        const parts = content.split(OTP_TIME_REGEX);
        return (
          <>
            {parts[0]}
            <Text component="span" fw={600} size="sm" c="blue.6">
              {formatTime(secondsLeft)}s
            </Text>
            {parts[2]}
          </>
        );
      }
      return (
        <>
          {content}
          <Text component="span" fw={600} size="sm" c="blue.6">
            {formatTime(secondsLeft)}s
          </Text>
        </>
      );
    }
    return content;
  }, [content, countdownInSecond, secondsLeft]);

  const modalStyles = useMemo(
    () => ({
      header: { paddingBottom: rem(12) },
      title: {
        fontSize: rem(18),
        lineHeight: rem(24),
        color: 'var(--mantine-color-neutral-9)',
        fontWeight: 700,
      },
      overlay: { zIndex: 1500 },
      inner: { zIndex: 1500 },
    }),
    []
  );

  const handleContactCustomerSupport = useCallback(() => {
    if (typeof window !== 'undefined') {
      const isMobile = MOBILE_REGEX.test(window.navigator.userAgent);
      if (isMobile) {
        window.location.href = `tel:${AHAMOVE_HOTLINE}`;
      } else {
        window.open('https://ahamove.com/customer/help-center', '_blank');
      }
    }
  }, []);

  const handleConfirm = () => {
    if (errorCode === 'USER_DEACTIVATED') {
      handleContactCustomerSupport();
    } else {
      onConfirm?.();
    }
    onClose();
  };
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={rem(343)}
      centered
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={title}
      styles={modalStyles}
    >
      <Text size="sm" fw={500} c="neutral.6" mb="md">
        {detailContent}
      </Text>
      <Stack gap={4}>
        <Button onClick={handleConfirm} size="sm" h={rem(48)} fullWidth>
          {confirmLabel}
        </Button>
        {!needCancelButton && (
          <Button
            onClick={onClose}
            size="sm"
            h={rem(48)}
            fullWidth
            c="neutral.7"
            variant="transparent"
          >
            {tActions('cancel')}
          </Button>
        )}
      </Stack>
    </Modal>
  );
}
